import { useRef, useCallback } from 'react';

/**
 * Enables click-and-drag horizontal scrolling with momentum on a container.
 * Returns a ref callback to attach to the scrollable element.
 * Suppresses click events on children when a drag occurred.
 * Prevents native browser image/element drag so dragging over images scrolls
 * the container instead of triggering the browser's drag-and-drop behaviour.
 */
export function useDragScroll() {
    const state = useRef({
        isDown: false,
        startX: 0,
        scrollLeft: 0,
        didDrag: false,
        velocity: 0,
        lastX: 0,
        lastTime: 0,
        animFrame: 0,
    });
    const cleanupRef = useRef<(() => void) | null>(null);

    const ref = useCallback((el: HTMLDivElement | null) => {
        cleanupRef.current?.();
        cleanupRef.current = null;

        if (!el) return;

        const s = state.current;

        const stopMomentum = () => {
            if (s.animFrame) {
                cancelAnimationFrame(s.animFrame);
                s.animFrame = 0;
            }
        };

        const onSelectStart = (e: Event) => {
            if (s.isDown) e.preventDefault();
        };

        // Prevent native browser image/element drag (dragstart fires when the
        // browser would start a drag-and-drop operation on an image or other
        // draggable element). Without this, dragging over a scene thumbnail
        // triggers the OS/browser native drag instead of scrolling (#323).
        const onDragStart = (e: DragEvent) => {
            e.preventDefault();
        };

        const startMomentum = () => {
            stopMomentum();
            const friction = 0.95;
            const step = () => {
                if (Math.abs(s.velocity) < 0.5) {
                    s.velocity = 0;
                    return;
                }
                el.scrollLeft += s.velocity;
                s.velocity *= friction;
                s.animFrame = requestAnimationFrame(step);
            };
            s.animFrame = requestAnimationFrame(step);
        };

        const onMouseDown = (e: MouseEvent) => {
            if (e.button !== 0) return;
            stopMomentum();
            s.isDown = true;
            s.didDrag = false;
            s.startX = e.pageX;
            s.scrollLeft = el.scrollLeft;
            s.velocity = 0;
            s.lastX = e.pageX;
            s.lastTime = Date.now();
            el.style.cursor = 'grabbing';
            // Prevent text/content selection during drag (#319)
            document.body.style.userSelect = 'none';
        };

        const onMouseMove = (e: MouseEvent) => {
            if (!s.isDown) return;
            e.preventDefault();
            const dx = e.pageX - s.startX;
            if (Math.abs(dx) > 3) {
                s.didDrag = true;
            }
            el.scrollLeft = s.scrollLeft - dx;

            // Track velocity from recent movement
            const now = Date.now();
            const dt = now - s.lastTime;
            if (dt > 0) {
                s.velocity = (s.lastX - e.pageX) / dt * 16;
            }
            s.lastX = e.pageX;
            s.lastTime = now;
        };

        const onMouseUp = () => {
            if (!s.isDown) return;
            s.isDown = false;
            el.style.cursor = '';
            document.body.style.userSelect = '';
            if (s.didDrag && Math.abs(s.velocity) > 1) {
                startMomentum();
            }
        };

        const onClick = (e: MouseEvent) => {
            if (s.didDrag) {
                e.stopPropagation();
                e.preventDefault();
                s.didDrag = false;
            }
        };

        el.addEventListener('mousedown', onMouseDown);
        el.addEventListener('selectstart', onSelectStart);
        el.addEventListener('dragstart', onDragStart);
        window.addEventListener('mousemove', onMouseMove);
        window.addEventListener('mouseup', onMouseUp);
        el.addEventListener('click', onClick, true);

        cleanupRef.current = () => {
            stopMomentum();
            // Restore body userSelect in case cleanup happens mid-drag
            document.body.style.userSelect = '';
            el.removeEventListener('mousedown', onMouseDown);
            el.removeEventListener('selectstart', onSelectStart);
            el.removeEventListener('dragstart', onDragStart);
            window.removeEventListener('mousemove', onMouseMove);
            window.removeEventListener('mouseup', onMouseUp);
            el.removeEventListener('click', onClick, true);
        };
    }, []);

    return ref;
}
